import {
  useCallback, useState, FC, PropsWithChildren, Ref,
} from 'react';

import styled from 'styled-components';

import { Icon } from './Icon';
import type {
  AlertType,
  AlertProps,
} from './types';
import { Tooltip } from '../Tooltip';

export const StyledAlert = styled.div<{
  $alertType: AlertType;
  $isNoMargins: boolean;
  $hasButton: boolean;
}>(({
  $alertType,
  $isNoMargins,
  $hasButton,

  theme: {
    alert,

    indents: {
      INDENT_3,
    },
  },
}) => ({
  boxSizing: 'border-box',
  marginTop: $isNoMargins ? 0 : INDENT_3,
  marginBottom: $isNoMargins ? 0 : INDENT_3,
  padding: alert.PADDING,
  borderWidth: alert.BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: alert.TYPES[$alertType].colorBorder,
  borderLeftColor: alert.TYPES[$alertType].color,
  borderRadius: alert.BORDER_RADIUS,
  backgroundColor: alert.TYPES[$alertType].bg,
  textAlign: 'left',
  minHeight: $hasButton ? 63 : undefined,
  position: 'relative',
}));

export const StyledTitleBlock = styled.div({
  display: 'flex',
});

export const StyledTitle = styled.div(({
  theme: {
    colors: {
      TEXT_PRIMARY,
    },

    fonts,

    alert: {
      TITLE_FONT,
    },
  },
}) => ({
  ...fonts[TITLE_FONT],
  flex: 1,
  color: TEXT_PRIMARY,
}));

export const StyledButtonWrapper = styled.div(({
  theme: {
    indents: {
      INDENT_1,
      INDENT_2,
    },
  },
}) => ({
  float: 'right',
  paddingTop: INDENT_1,
  paddingBottom: INDENT_1,
  paddingLeft: INDENT_2,
}));

export const StyledContent = styled.div<{ $withoutIcon: boolean }>(({
  $withoutIcon,
  theme: {
    colors: {
      TEXT_PRIMARY,
    },
    fonts: {
      DEFAULT_COMPACT,
    },
    indents: {
      INDENT_4,
    },
  },
}) => ({
  ...DEFAULT_COMPACT,
  color: TEXT_PRIMARY,
  paddingTop: '2px',
  paddingLeft: $withoutIcon ? '' : INDENT_4,
}));

export const CloseButton = styled.button<{ withWrapper?: boolean }>(({
  theme: {
    colors,
  },
  withWrapper = undefined,
}) => ({
  position: withWrapper ? 'static' : 'absolute',
  top: withWrapper ? 0 : 4,
  right: withWrapper ? 0 : 4,
  margin: 0,
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.NEUTRAL_DEFAULT,
  padding: 10,
  background: 'transparent',
}));

const ButtonWrapper = styled.div({
  position: 'absolute',
  top: 4,
  right: 4,
});

// eslint-disable-next-line react/function-component-definition
export const Alert:FC<PropsWithChildren<AlertProps>> = ({
  type,
  className = undefined,
  title = undefined,
  button = undefined,
  noMargins = false,
  children = undefined,
  withoutIcon = false,
  withoutClose = false,
  onClose,
  tooltip = undefined,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const onCloseHandler = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const renderButton = useCallback((withWrapper: boolean) => (
    <CloseButton withWrapper={withWrapper} type="button" onClick={onCloseHandler}>
      <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 0.7L7.3 0L4 3.3L0.7 0L0 0.7L3.3 4L0 7.3L0.7 8L4 4.7L7.3 8L8 7.3L4.7 4L8 0.7Z" />
      </svg>
    </CloseButton>
  ), [onCloseHandler]);

  if (!isOpen) {
    return null;
  }

  return (
    <StyledAlert
      className={className}
      $alertType={type}
      $isNoMargins={noMargins}
      $hasButton={Boolean(button)}
    >
      {
        button && (
          <StyledButtonWrapper>
            {button}
          </StyledButtonWrapper>
        )
      }

      {
        title && (
          <StyledTitleBlock>
            {!withoutIcon && (
            <Icon
              type={type}
            />
            )}

            <StyledTitle>
              {title}
            </StyledTitle>
          </StyledTitleBlock>
        )
      }

      {
        children && (
          <StyledContent $withoutIcon={withoutIcon || !title}>
            {
              !title && !withoutIcon && (
                <Icon
                  type={type}
                />
              )
            }

            <span>
              {children}
            </span>
          </StyledContent>
        )
      }

      {!withoutClose && !button && (
        tooltip ? (
          <Tooltip tooltip={tooltip}>
            {({ innerProps, ref }) => (
              <ButtonWrapper {...innerProps} ref={ref as Ref<HTMLDivElement>}>
                {renderButton(true)}
              </ButtonWrapper>
            )}
          </Tooltip>
        ) : (
          renderButton(false)
        )
      )}
    </StyledAlert>
  );
};
